/**
 * Storage quota for uploads: checked before an upload, counted after it.
 * Shared by the canvas drop zone and Partnr's image attachments, so both
 * count toward the same limit.
 */
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export const checkStorageLimit = async (userId: string, fileSizeMB: number): Promise<boolean> => {
  const { data: settings } = await supabase
    .from('user_settings')
    .select('storage_used_mb, storage_limit_mb')
    .eq('user_id', userId)
    .maybeSingle();

  if (!settings) return true; // Allow if no settings found

  const newUsage = settings.storage_used_mb + fileSizeMB;
  if (newUsage > settings.storage_limit_mb) {
    toast.error(`Storage limit reached (${settings.storage_limit_mb}MB). Delete some files to upload more.`);
    return false;
  }
  return true;
};

export const updateStorageUsage = async (userId: string, fileSizeMB: number): Promise<void> => {
  const { data: settings } = await supabase
    .from('user_settings')
    .select('storage_used_mb, storage_limit_mb, storage_notified_50, storage_notified_100')
    .eq('user_id', userId)
    .maybeSingle();

  if (!settings) return;

  const newUsage = settings.storage_used_mb + fileSizeMB;
  const usagePercent = (newUsage / settings.storage_limit_mb) * 100;

  const updates: Record<string, unknown> = {
    storage_used_mb: newUsage,
  };

  if (usagePercent >= 50 && !settings.storage_notified_50) {
    toast.info(`You've used 50% of your storage (${newUsage.toFixed(1)}MB / ${settings.storage_limit_mb}MB)`);
    updates.storage_notified_50 = true;
  }

  if (usagePercent >= 90 && !settings.storage_notified_100) {
    toast.warning(`You're almost out of storage (${usagePercent.toFixed(0)}% used)`);
    updates.storage_notified_100 = true;
  }

  await supabase
    .from('user_settings')
    .update(updates)
    .eq('user_id', userId);
};
